export const formatDateTime = (date) => {
  if (!date) return "";
  return date.toLocaleString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatDate = (date) => {
  if (!date) return "Never";

  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

export const formatSeconds = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

  return parts.join(" ");
};

// Convert Firebase time to Date object
export const firebaseTimeToDate = (firebaseTime) => {
  let milliseconds;
  let date;

  try {
    if (!firebaseTime) {
      return new Date(0);
    }

    // Try Firebase timestamp format: {seconds: number} or {_seconds: number, _nanoseconds: number}
    if (typeof firebaseTime === "object" && !(firebaseTime instanceof Date)) {
      // Firestore Timestamp with toDate() method
      if (typeof firebaseTime.toDate === "function") {
        date = firebaseTime.toDate();
      }
      // Firestore Timestamp with seconds property (without underscore)
      else if (
        "seconds" in firebaseTime &&
        typeof firebaseTime.seconds === "number"
      ) {
        date = new Date(firebaseTime.seconds * 1000);
      }
      // Firestore Timestamp with _seconds and _nanoseconds properties
      else if ("_seconds" in firebaseTime && "_nanoseconds" in firebaseTime) {
        milliseconds =
          firebaseTime._seconds * 1000 + firebaseTime._nanoseconds / 1000000;
        date = new Date(milliseconds);
      } else {
        throw new Error("Unsupported Firebase timestamp format");
      }
    }
    // Try plain number (Unix timestamp in milliseconds or seconds)
    else if (typeof firebaseTime === "number") {
      // Check if it's in seconds (less than a reasonable timestamp in milliseconds)
      // Timestamps before year 2001 in milliseconds would be less than 1000000000000
      if (firebaseTime < 1000000000000) {
        // Assume it's in seconds (UTC), convert to milliseconds
        milliseconds = firebaseTime * 1000;
      } else {
        // Assume it's already in milliseconds (UTC)
        milliseconds = firebaseTime;
      }
      date = new Date(milliseconds);
    }
    // Try string (could be a number string or ISO date string)
    else if (typeof firebaseTime === "string") {
      // Try parsing as number first (UTC timestamp)
      const numValue = Number(firebaseTime);
      if (!isNaN(numValue) && firebaseTime.trim() !== "") {
        if (numValue < 1000000000000) {
          // Assume it's in seconds (UTC), convert to milliseconds
          milliseconds = numValue * 1000;
        } else {
          // Assume it's already in milliseconds (UTC)
          milliseconds = numValue;
        }
        date = new Date(milliseconds);
      } else {
        // Try parsing as ISO date string
        // If it ends with 'Z' or has timezone info, it's already UTC-aware
        // Otherwise, append 'Z' to treat as UTC
        let dateString = firebaseTime.trim();
        if (
          !dateString.includes("Z") &&
          !dateString.includes("+") &&
          !dateString.includes("-", 10)
        ) {
          // No timezone info, append 'Z' to treat as UTC
          dateString = dateString + "Z";
        }
        date = new Date(dateString);
        if (isNaN(date.getTime())) {
          throw new Error("Invalid date string format");
        }
      }
    }
    // Try Date object
    else if (firebaseTime instanceof Date) {
      date = firebaseTime;
    } else {
      throw new Error("Unsupported timestamp format");
    }

    // Validate the date
    if (isNaN(date.getTime())) {
      throw new Error("Invalid date");
    }

    return date;
  } catch (error) {
    console.error(
      "Error converting Firebase time to Date:",
      error,
      firebaseTime
    );
    // Return epoch date as fallback
    return new Date(0);
  }
};

// Convert Firebase time to timestamp in milliseconds
export const firebaseTimeToTimestamp = (firebaseTime) => {
  const date = firebaseTimeToDate(firebaseTime);
  return date.getTime();
};

export const formatFirebaseTime = (firebaseTime) => {
  try {
    const date = firebaseTimeToDate(firebaseTime);

    // Format options - display in user's local timezone
    // The timestamp is UTC-based, but we display it in local timezone for better UX
    const options = {
      weekday: "long", // Show full day name (Monday, Tuesday, etc.)
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // Enable AM/PM format
    };

    return date.toLocaleDateString("en-US", options);
  } catch (error) {
    console.error("Error formatting Firebase time:", error, firebaseTime);
    // Return a fallback or empty string
    return "";
  }
};

